import React, { useEffect, useState, useContext, useRef } from 'react'
import axios from 'axios'
import DataTable from 'react-data-table-component'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass, faSquarePlus, faEdit, faFileCsv, faComputer, faRecycle } from '@fortawesome/free-solid-svg-icons'
import CustomLoader from '../layouts/CustomLoader'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import AuthContext from '../../middlewares/AuthContext'
import jwt_decode from 'jwt-decode'
import { DateTime } from 'luxon'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import DatePicker from 'react-datepicker'
import { useTranslation } from 'react-i18next'
import usePaginationComponentOptions from '../layouts/paginationComponentOptions'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND = '#6eb446'

// ── Interfaces ────────────────────────────────────────────────────────────────
interface AuthCtx {
  getHeaders : () => Promise<Record<string, string>>
  token      : string | null
}

interface Product {
  name            : string
  list_price      : string
  list_price_mx   : string
  list_price_2    : string
  list_price_mx_2 : string
}
interface Plan    { name: string; Product: Product }
interface Status  { name: string }
interface Partner { name: string }

interface PackageUser { Partner?: Partner | null }

interface LicensePackage {
  serial_number : string
  UsertypeId    : number
  StatusId      : number
  Status        : Status
  User?         : PackageUser
  Products      : Product[]
  Plans         : Plan[]
}

interface Activation {
  device            : string
  number_activation : number
  createdAt         : string
  Product           : Product
}

interface PospaymentLicense { price: string; createdAt: string }
interface Pospayment        { PospaymentLicense?: PospaymentLicense }
interface LicenseFirst      { createdAt: string }

interface License {
  id            : number
  product_key   : string
  StatusId      : number
  Status        : Status
  createdAt     : string
  expireAt      : string
  Package       : LicensePackage
  User?         : { email: string } | null
  Activations   : Activation[]
  Renovateds    : any[]
  Pospayments   : Pospayment[]
  license_first : LicenseFirst[]
}

interface StatusOption { id: number; name: string }

// ── Pill button helpers ───────────────────────────────────────────────────────
const pill = (bg: string): React.CSSProperties => ({
  display        : 'inline-flex',
  alignItems     : 'center',
  gap            : '0.4rem',
  padding        : '0.45rem 1.1rem',
  borderRadius   : '9999px',
  backgroundColor: bg,
  color          : '#fff',
  fontWeight     : 700,
  fontSize       : '0.85rem',
  border         : 'none',
  cursor         : 'pointer',
  boxShadow      : `0 3px 10px ${bg}55`,
  transition     : 'filter 0.15s, transform 0.15s',
  textDecoration : 'none',
  whiteSpace     : 'nowrap' as const,
})

const hoverIn  = (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.filter = 'brightness(1.09)'; e.currentTarget.style.transform = 'translateY(-1px)' }
const hoverOut = (e: React.MouseEvent<HTMLElement>) => { e.currentTarget.style.filter = 'brightness(1)';    e.currentTarget.style.transform = 'translateY(0)' }

// ── Shared helpers ────────────────────────────────────────────────────────────
const expiryMonths = (expireAt: string) =>
  (DateTime.fromISO(expireAt).diff(DateTime.now(), ['months']).toObject().months ?? 0)

const canRenovate = (row: License) =>
  row.Activations.length > 0 && expiryMonths(row.expireAt) < 1

const statusBadgeCls = (statusId: number) => {
  if (statusId === 1) return 'badge text-bg-primary'
  if (statusId === 2) return 'badge text-bg-danger'
  return 'badge text-bg-warning'
}

// ── Shared cell renderers ─────────────────────────────────────────────────────
const CellPartner    = (row: License) => row.Package.User?.Partner
  ? <p className="text-capitalize mb-0">{row.Package.User.Partner.name}</p>
  : <p>-</p>

const CellPackage    = (row: License) => <p className="text-capitalize mb-0">{row.Package.serial_number}</p>
const CellProductKey = (row: License) => <p className="text-capitalize mb-0 w-100 text-start">{row.product_key}</p>
const CellCompat     = (row: License) =>
  <>{row.Package.Products.map((e, i) => <p className="text-capitalize mb-0 w-100 text-start" key={`com-${i}`}>{e.name}</p>)}</>

const CellDevice = (row: License) =>
  row.Activations.length > 0
    ? <p><b>{row.Activations[0].device}</b></p>
    : <p>-</p>

const CellExpireAt = (row: License, aboutExpire: string, expiredLabel: string) => {
  const months = expiryMonths(row.expireAt)
  const date   = DateTime.fromISO(row.expireAt, { zone: 'utc' }).toFormat('yyyy-MM-dd')
  let label = '', cls = ''
  if (months < 1 && months >= 0) { label = aboutExpire; cls = 'text-warning' }
  else if (months < 0)           { label = expiredLabel; cls = 'text-danger' }
  return <>
    <p className="text-capitalize mb-0">{date}</p>
    {label && <p className={cls}><b>{label}</b></p>}
  </>
}

const CellStatus = (row: License, tFn: (k: string) => string) =>
  row.Package.StatusId === 2 || row.Package.StatusId === 5
    ? <p style={{ fontSize: 14 }} className="text-danger">
        Package is: <b className="badge text-bg-danger text-capitalize">{row.Package.Status.name}</b>
      </p>
    : <p style={{ fontSize: 12 }} className={`text-capitalize mb-0 ${statusBadgeCls(row.StatusId)}`}>
        {tFn(`status.${row.Status.name}`)}
      </p>

// ── Component ─────────────────────────────────────────────────────────────────
const Licenses: React.FC = () => {
  const { getHeaders, token }      = useContext(AuthContext) as AuthCtx
  const tokenDecode: any           = token ? jwt_decode(token) : null
  const { t, i18n }                = useTranslation()
  const paginationComponentOptions = usePaginationComponentOptions()

  const role: string = tokenDecode?.roles?.[0]?.name ?? ''

  const [startDate, setStartDate]   = useState<Date>(new Date())
  const [dateValid, setDateValid]   = useState(false)
  const [statuses, setStatuses]     = useState<StatusOption[]>([])

  const [selectedRowIndividual, setSelectedRowIndividual] = useState<License | null>(null)
  const [selectedRows, setSelectedRows]                   = useState<License[]>([])
  const [toggledClearRows]                                = useState(false)
  const [licensesCsv, setLicensesCsv]                     = useState('10')
  const [data, setData]                                   = useState<License[]>([])
  const [currency, setCurrency]                           = useState('')
  const [loading, setLoading]                             = useState(false)
  const [totalRows, setTotalRows]                         = useState(0)
  const [perPage, setPerPage]                             = useState(50)
  const [columns, setColumns]                             = useState<any[]>([])
  const [_product_key, set_product_key]                   = useState('')
  const [_useremail, set_useremail]                       = useState('')
  const [_package, set_package]                           = useState('')
  const [searchOpen, setSearchOpen]                       = useState(false)
  const [showUpdateKeys, setShowUpdateKeys]               = useState(false)
  const [showRenovateKeys, setShowRenovateKeys]           = useState(false)
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false)

  const download              = useRef<HTMLButtonElement>(null)
  const downloadCls           = useRef<HTMLButtonElement>(null)
  const updatesBtn            = useRef<HTMLButtonElement>(null)
  const renovateBtn           = useRef<HTMLButtonElement>(null)
  const renovateIndividualBtn = useRef<HTMLButtonElement>(null)

  const formSchema = Yup.object().shape({
    time: Yup.number().min(1).max(2).required(),
  })

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchLicenses = async (
    page       : number,
    perPageVal   = perPage,
    productKey   = _product_key,
    userEmail    = _useremail,
    packageVal   = _package,
  ) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res = await axios.get<{ message: License[]; total: number; currency: string }>(
        `${url}licenses?page=${page}&per_page=${perPageVal}&product_key=${productKey}&useremail=${userEmail}&package=${packageVal}`,
        { headers }
      )
      setData(res.data.message)
      setTotalRows(res.data.total)
      setCurrency(res.data.currency)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  const handlePageChange    = (page: number) => fetchLicenses(page)

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage)
    await fetchLicenses(page, newPerPage)
  }

  const handleSearch = async (values: { product_key: string; useremail: string; package: string }) => {
    set_product_key(values.product_key)
    set_useremail(values.useremail)
    set_package(values.package)
    await fetchLicenses(1, perPage, values.product_key, values.useremail, values.package)
  }

  const fetchCsv = async () => {
    download.current?.click()
    try {
      const headers = await getHeaders()
      const res = await axios.get(`${url}licenses/all/csv/${licensesCsv}`, { headers })
      const link        = document.createElement('a')
      link.href         = window.URL.createObjectURL(new Blob([res.data]))
      link.setAttribute('download', `licenses-all-${licensesCsv}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setTimeout(() => downloadCls.current?.click(), 500)
    }
  }

  const deactivate = async (productKey: string, device: string) => {
    const result = await Swal.fire({
      title            : `${t('licenses.customer.str_remove_license')} ${device}`,
      text             : t('licenses.customer.str_this_license'),
      showCancelButton : true,
      confirmButtonText: t('licenses.customer.remove'),
      customClass      : { confirmButton: 'btn btn-outline-danger', cancelButton: 'btn btn-primary mx-2' },
      buttonsStyling   : false,
      showClass        : { popup: 'animate__animated animate__fadeInDown' },
      hideClass        : { popup: 'animate__animated animate__fadeOutUp' },
    })
    if (!result.isConfirmed) return
    try {
      const headers = await getHeaders()
      const res = await axios.post(`${url}licenses/deactivate`, { license: productKey }, { headers })
      Swal.fire({ icon: 'success', title: res.data.message })
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    }
  }

  const getStatuses = async () => {
    try {
      const headers = await getHeaders()
      const res = await axios.get<{ message: StatusOption[] }>(`${url}statuses`, { headers })
      setStatuses(res.data.message)
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    }
  }

  const handleRenovate = async (params: { time: number }, onlyOne = false) => {
    setLoading(true)
    const rows = onlyOne
      ? [selectedRowIndividual!.product_key]
      : selectedRows.map(e => e.product_key)
    try {
      const headers = await getHeaders()
      const res = await axios.post(`${url}licenses/renovate`, { keys: rows, time: params.time }, { headers })
      if (!res.data.partner) {
        window.location.href = res.data.url
      } else {
        Swal.fire({ icon: 'success', title: 'Renovate successfully (charge to next month)' })
        fetchLicenses(1)
        setResetPaginationToggle(prev => !prev)
        renovateBtn.current?.click()
        renovateIndividualBtn.current?.click()
      }
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  const updateLicenses = async (params: { StatusId: number }) => {
    const expireAt = DateTime.fromJSDate(startDate)
    if (!expireAt.isValid) { setDateValid(false); return }
    setLoading(true)
    try {
      const headers    = await getHeaders()
      const licenseIds = selectedRows.map(e => e.id)
      const res = await axios.put(
        `${url}licenses/updates-masive/`,
        { ...params, expireAt: expireAt.toFormat('yyyy-MM-dd'), licenses: licenseIds },
        { headers }
      )
      Swal.fire({ icon: 'success', title: res.data.response })
      setResetPaginationToggle(prev => !prev)
      updatesBtn.current?.click()
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err?.response?.data?.response || 'Error' })
    } finally {
      setLoading(false)
    }
  }

  const sumListPrices = (rows: License[], time: number | string) => {
    const t1  = parseInt(String(time)) === 1
    const sum = rows.reduce((acc, row) => {
      const p = row.Activations[0]?.Product
      if (!p) return acc
      const price = t1
        ? (currency === 'mxn' ? p.list_price_mx   : p.list_price)
        : (currency === 'mxn' ? p.list_price_mx_2 : p.list_price_2)
      return acc + parseFloat(price)
    }, 0)
    return role === 'partner'
      ? t('licenses.partner.charge_month')
      : `${t('licenses.price_for_renovation')}: $${sum.toFixed(2)}`
  }

  // ── Row selection ──────────────────────────────────────────────────────────
  const handleChange = ({ selectedRows: rows }: { selectedRows: License[] }) => {
    setSelectedRows(rows)
    const el = document.querySelector('.rdt_TableHeader') as HTMLElement | null
    if (el) el.classList.toggle('d-none', rows.length === 0)
  }

  const rowDisabledCriteria = (row: License) => !canRenovate(row)

  const conditionalRowStyles = [
    {
      when : (row: License) => expiryMonths(row.expireAt) < 0,
      style: { borderLeft: '3px solid #ef4444' },
    },
    {
      when : (row: License) => expiryMonths(row.expireAt) >= 0 && expiryMonths(row.expireAt) < 1,
      style: { borderLeft: '3px solid #f59e0b' },
    },
  ]

  // ── Context actions ────────────────────────────────────────────────────────
  const contextActions = React.useMemo(() =>
    role !== 'admin'
      ? <button style={pill(BRAND)} onMouseEnter={hoverIn} onMouseLeave={hoverOut}
          data-bs-toggle="modal" data-bs-target="#renovate">
          {t('licenses.customer.renovate')}
        </button>
      : <button style={pill('#0ea5e9')} onMouseEnter={hoverIn} onMouseLeave={hoverOut}
          data-bs-toggle="modal" data-bs-target="#updates">
          Update Licenses
        </button>
  , [data, selectedRows, toggledClearRows])

  // ── Column builders ────────────────────────────────────────────────────────
  const actionDropdown = (row: License, withDeactivate: boolean) => {
    const canRenov = canRenovate(row)
    return (
      <div className="dropdown mb-2">
        <a className="btn btn-sm btn-primary btn-shadow dropdown-toggle"
          href="#!" role="button" data-bs-toggle="dropdown" aria-expanded="false" style={{ width: 120 }}>
          {t('licenses.customer.options')}
        </a>
        <ul className="dropdown-menu" style={{ width: 220 }}>
          {withDeactivate && (
            <li>
              {row.Status.name === 'activated'
                ? <a href="#!" className="dropdown-item fw-bold"
                    onClick={() => deactivate(row.product_key, row.Activations[0]?.device)}>
                    <div className="row g-0">
                      <div className="col-2"><FontAwesomeIcon icon={faComputer}/></div>
                      <div className="col-10">{t('licenses.customer.remove_from_device')}</div>
                    </div>
                  </a>
                : <a href="#!" className="dropdown-item fw-bold text-muted disabled opacity-25">
                    <div className="row g-0">
                      <div className="col-2"><FontAwesomeIcon icon={faComputer}/></div>
                      <div className="col-10">{t('licenses.customer.remove_from_device')}</div>
                    </div>
                  </a>
              }
            </li>
          )}
          <li>
            {canRenov
              ? <a href="#!" className="dropdown-item fw-bold"
                  data-bs-toggle="modal" data-bs-target="#renovateIndividual"
                  onClick={() => setSelectedRowIndividual(row)}>
                  <div className="row g-0">
                    <div className="col-2"><FontAwesomeIcon icon={faRecycle}/></div>
                    <div className="col-10">{t('licenses.customer.renovate')}</div>
                  </div>
                </a>
              : <a href="#!" className="dropdown-item fw-bold disabled opacity-25">
                  <div className="row g-0">
                    <div className="col-2"><FontAwesomeIcon icon={faRecycle}/></div>
                    <div className="col-10">{t('licenses.customer.renovate')}</div>
                  </div>
                </a>
            }
          </li>
        </ul>
      </div>
    )
  }

  const buildAdminColumns = () => [
    { name: 'Partner',    cell: CellPartner },
    { name: 'Package #S/N', cell: CellPackage },
    { minWidth: '270px', name: 'Product Key', cell: CellProductKey },
    { name: 'Created At', cell: (row: License) =>
        <p className="text-capitalize mb-0">
          {DateTime.fromISO(row.createdAt, { zone: 'utc-6' }).setLocale(i18n.language).toFormat('yyyy-MM-dd HH:mm:ss')}
        </p>
    },
    { name: 'Email user own', cell: (row: License) => row.User ? <p>{row.User.email}</p> : <p>-</p> },
    { name: 'User type', cell: (row: License) => <>
        {row.Package.UsertypeId === 1 && <p><b className="text-uppercase">Admin</b></p>}
        {row.Package.UsertypeId === 2 && <p><b className="text-uppercase">Partner</b></p>}
        {row.Package.UsertypeId === 3 && <>
          <p><b className="text-uppercase">Customer</b></p>
          <p>Plan: {row.Package.Plans[0]?.name || '-'}</p>
          <p>Soft: {row.Package.Plans[0]?.Product?.name || '-'}</p>
        </>}
      </>
    },
    { name: 'Current device', cell: CellDevice },
    { name: 'Number activations', cell: (row: License) =>
        row.Activations.length > 0
          ? <p><b>Number activation</b>: {row.Activations[0].number_activation}</p>
          : <p>-</p>
    },
    { name: 'Current activation', cell: (row: License) => {
        if (!row.Activations.length) return <div className="w-100 text-start"><p>-</p></div>
        const da = DateTime.fromISO(row.Activations[0].createdAt, { zone: 'utc-6' }).setLocale(i18n.language).toFormat('yyyy-MM-dd HH:mm:ss')
        return <div className="w-100 text-start"><p>{da}</p><p className="mb-0"><b>Product: </b></p><p>{row.Activations[0].Product.name}</p></div>
    }},
    { name: 'First activation', cell: (row: License) => {
        if (!row.license_first.length) return <div className="w-100 text-start"><p>-</p></div>
        const da = DateTime.fromISO(row.license_first[0].createdAt, { zone: 'utc-6' }).setLocale(i18n.language).toFormat('yyyy-MM-dd HH:mm:ss')
        return <div className="w-100 text-start"><p>{da}</p></div>
    }},
    { name: 'Renovations', cell: (row: License) => <p className="text-capitalize mb-0">{row.Renovateds.length}</p> },
    { name: 'Expire At',   cell: (row: License) => CellExpireAt(row, t('licenses.customer.is_about_expire'), t('licenses.customer.expire')) },
    { name: 'Status',      cell: (row: License) => CellStatus(row, t) },
    { minWidth: '120px', name: 'Actions', cell: (row: License) =>
        <div className="d-flex w-100 justify-content-center p-1">
          <Link to={`edit/${row.id}`} className="btn btn-success btn-shadow btn-sm">
            <FontAwesomeIcon icon={faEdit} className="me-1"/>
            <span className="fw-bold">Edit</span>
          </Link>
        </div>
    },
  ]

  const buildCustomerColumns = () => [
    { name: t('licenses.customer.partner'),           cell: CellPartner },
    { name: t('licenses.customer.package'),           cell: CellPackage },
    { minWidth: '270px', name: t('licenses.customer.product_key'), cell: CellProductKey },
    { minWidth: '100px', name: t('licenses.customer.compatibility'), cell: CellCompat },
    { name: t('licenses.customer.current_device'),    cell: CellDevice },
    { name: t('licenses.customer.number_activation'), cell: (row: License) =>
        row.Activations.length > 0
          ? <p><b>{t('licenses.customer.number_activation')}</b>: {row.Activations[0].number_activation}</p>
          : <p>-</p>
    },
    { name: 'Primer activación', cell: (row: License) => {
        if (!row.license_first.length) return <div className="w-100 text-start"><p>-</p></div>
        const da = DateTime.fromISO(row.license_first[0].createdAt, { zone: 'utc-6' }).setLocale(i18n.language).toFormat('yyyy-MM-dd HH:mm:ss')
        return <div className="w-100 text-start"><p>{da}</p></div>
    }},
    { name: t('licenses.customer.current_activation'), cell: (row: License) => {
        if (!row.Activations.length) return <div className="w-100 text-start"><p>-</p></div>
        const da = DateTime.fromISO(row.Activations[0].createdAt, { zone: 'utc' }).toFormat('yyyy-MM-dd HH:mm:ss')
        return <div className="w-100 text-start"><p>{da}</p><p className="mb-0"><b>{t('licenses.customer.product')}: </b></p><p>{row.Activations[0].Product.name}</p></div>
    }},
    { name: t('licenses.customer.expire_at'), cell: (row: License) => CellExpireAt(row, t('licenses.customer.is_about_expire'), t('licenses.customer.expire')) },
    { name: t('licenses.customer.status'),   cell: (row: License) => CellStatus(row, t) },
    { minWidth: '150px', name: t('licenses.customer.actions'), cell: (row: License) => actionDropdown(row, true) },
  ]

  const buildPartnerSubpartnerColumns = () => [
    { name: t('licenses.customer.partner'),   cell: CellPartner },
    { name: t('licenses.customer.package'),   cell: CellPackage },
    { minWidth: '270px', name: t('licenses.customer.product_key'), cell: CellProductKey },
    { minWidth: '100px', name: t('licenses.customer.compatibility'), cell: CellCompat },
    { name: t('licenses.partner.created_at'), cell: (row: License) => {
        const d = new Date(row.createdAt)
        return <p className="text-capitalize mb-0">{d.toISOString().slice(0,10)} {d.toISOString().slice(11,19)}</p>
    }},
    { name: t('licenses.partner.email_user_owner'), cell: (row: License) =>
        row.User ? <p>{row.User.email}</p> : <p>-</p>
    },
    { name: 'Charges', cell: (row: License) =>
        <ul className="ps-1">
          {row.Pospayments.length > 0
            ? row.Pospayments.map((e, i) => {
                const createdAt = DateTime.fromISO(e.PospaymentLicense?.createdAt ?? '').setLocale(i18n.language).toFormat('yyyy LLLL dd')
                return <li className="mb-1 border-bottom" key={`price-${i}`}>{e.PospaymentLicense?.price ?? '-'}$ {createdAt}</li>
              })
            : <li><p>-</p></li>
          }
        </ul>
    },
    { name: t('licenses.customer.current_device'), cell: CellDevice },
    { name: t('licenses.customer.number_activation'), cell: (row: License) =>
        row.Activations.length > 0
          ? <p><b>Number activation</b>: {row.Activations[0].number_activation}</p>
          : <p>-</p>
    },
    { name: t('licenses.customer.current_activation'), cell: (row: License) => {
        if (!row.Activations.length) return <div className="w-100 text-start"><p>-</p></div>
        const d  = new Date(row.Activations[0].createdAt)
        const da = `${d.toISOString().slice(0,10)} ${d.toISOString().slice(11,19)}`
        return <div className="w-100 text-start"><p>{da}</p><p className="mb-0"><b>Product: </b></p><p>{row.Activations[0].Product.name}</p></div>
    }},
    { name: t('licenses.partner.first_activation'), cell: (row: License) => {
        if (!row.license_first.length) return <div className="w-100 text-start"><p>-</p></div>
        const d  = new Date(row.license_first[0].createdAt)
        const da = `${d.toISOString().slice(0,10)} ${d.toISOString().slice(11,19)}`
        return <div className="w-100 text-start"><p>{da}</p></div>
    }},
    { name: t('licenses.customer.expire_at'), cell: (row: License) => CellExpireAt(row, t('licenses.customer.is_about_expire'), t('licenses.customer.expire')) },
    { name: t('licenses.customer.status'),   cell: (row: License) => CellStatus(row, t) },
    { minWidth: '150px', name: t('licenses.customer.actions'), cell: (row: License) => actionDropdown(row, false) },
  ]

  // ── Init ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    switch (role) {
      case 'customer':   setColumns(buildCustomerColumns());          break
      case 'partner':    setColumns(buildPartnerSubpartnerColumns()); break
      case 'subpartner': setColumns(buildPartnerSubpartnerColumns()); break
      case 'admin':      setColumns(buildAdminColumns());             break
    }
    fetchLicenses(1)
    getStatuses()
    const el = document.querySelector('.rdt_TableHeader') as HTMLElement | null
    if (el) el.classList.add('d-none')
  }, [])

  // ── JSX ────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Hidden trigger for CSV spinner modal */}
      <button type="button" className="d-none" data-bs-toggle="modal" data-bs-target="#staticBackdrop" ref={download} />

      {/* CSV loading spinner modal */}
      <div className="modal fade" id="staticBackdrop" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-sm">
          <div className="modal-content">
            <button type="button" className="d-none" data-bs-dismiss="modal" aria-label="Close" ref={downloadCls} />
            <div className="modal-body text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Admin: mass update modal */}
      <div className="modal fade" id="updates" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5">Edit licenses masive</h1>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" ref={updatesBtn} />
            </div>
            <div className="modal-body">
              <Formik initialValues={{ StatusId: 0 }} validationSchema={formSchema} onSubmit={updateLicenses}>
                {() => (
                  <Form>
                    <div className="row justify-content-center">
                      <div className="col-md-9">
                        <main className="p-4 border rounded bg-secondary-subtle shadow">
                          <div className="mb-3">
                            <label>Expire at</label>
                            <DatePicker
                              dateFormat="yyyy-MM-dd"
                              selected={startDate}
                              onChange={(v: Date | null) => v && setStartDate(v)}
                              className="form-control" id="expireAt" name="expireAt"
                            />
                            {dateValid && <p className="text-danger fw-bold mt-1">Invalid Date</p>}
                          </div>
                          <div className="mb-3">
                            <label>Status License</label>
                            <Field as="select" className="form-select text-capitalize" name="StatusId">
                              <option disabled value={0}>-Select option-</option>
                              {statuses.map((e, i) => <option key={`status-${i}`} value={e.id} className="text-capitalize">{e.name}</option>)}
                            </Field>
                            <ErrorMessage name="StatusId" component="div" className="field-error text-danger fw-bold text-capitalize" />
                          </div>
                          <div className="mt-4">
                            <button className="w-100 btn btn-lg btn-bd-primary btn-shadow mb-3" type="submit" disabled={loading}>
                              <span className="me-2 fw-bold">Update License</span>
                              {loading && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />}
                            </button>
                          </div>
                        </main>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>

              <div className="mt-3">
                <button
                  type="button"
                  style={pill('#0ea5e9')}
                  onMouseEnter={hoverIn}
                  onMouseLeave={hoverOut}
                  onClick={() => setShowUpdateKeys(prev => !prev)}
                  className="mb-2"
                >
                  {t('licenses.show_row_selected')}
                </button>
                {showUpdateKeys && (
                  <div className="border rounded p-2">
                    {selectedRows.map((e, i) => <p className="m-0" key={`uk-${i}`}>{e.product_key}</p>)}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">{t('licenses.close')}</button>
            </div>
          </div>
        </div>
      </div>

      {/* Renovate multi modal */}
      <div className="modal fade" id="renovate" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5">{t('licenses.renovated_masive')}</h1>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" ref={renovateBtn} disabled={loading} />
            </div>
            <Formik initialValues={{ time: 1 }} validationSchema={formSchema} onSubmit={values => handleRenovate(values, false)}>
              {({ values }) => (
                <Form>
                  <div className="modal-body">
                    <h4>{t('licenses.charge_stripe')}</h4>
                    <div className="row justify-content-center">
                      <div className="col-md-12">
                        <main className="p-4 border rounded bg-secondary-subtle shadow">
                          <div className="mb-3">
                            <label>{t('licenses.time_to_expire')}</label>
                            <Field as="select" className="form-select" name="time">
                              <option value={1}>1 {t('licenses.year')}</option>
                              <option value={2}>2 {t('licenses.years')}</option>
                            </Field>
                          </div>
                        </main>
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="d-flex align-items-center gap-3 mb-2 flex-wrap">
                        <button
                          type="button"
                          style={pill('#0ea5e9')}
                          onMouseEnter={hoverIn}
                          onMouseLeave={hoverOut}
                          onClick={() => setShowRenovateKeys(prev => !prev)}
                        >
                          {t('licenses.show_row_selected')}
                        </button>
                        <span className="fw-semibold">
                          {selectedRows.length > 0 && sumListPrices(selectedRows, values.time)}
                          <span className="ms-1 text-muted">{currency}</span>
                        </span>
                      </div>
                      {showRenovateKeys && (
                        <div className="border rounded p-2">
                          {selectedRows.map((e, i) => <p className="m-0" key={`rk-${i}`}>{e.product_key}</p>)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button disabled={loading} type="submit" className="btn btn-primary">
                      {t('licenses.customer.renovate')}
                      {loading && <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" />}
                    </button>
                    <button disabled={loading} type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                      {t('licenses.close')}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      {/* Renovate individual modal */}
      <div className="modal fade" id="renovateIndividual" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5">{t('licenses.renovated_individual')}</h1>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" ref={renovateIndividualBtn} disabled={loading} />
            </div>
            <Formik initialValues={{ time: 1 }} validationSchema={formSchema} onSubmit={values => handleRenovate(values, true)}>
              {({ values }) => (
                <Form>
                  <div className="modal-body">
                    <h4>{t('licenses.charge_stripe')}</h4>
                    <div className="row justify-content-center">
                      <div className="col-md-12">
                        <main className="p-4 border rounded bg-secondary-subtle shadow">
                          <div className="mb-3">
                            <label>{t('licenses.time_to_expire')}</label>
                            <Field as="select" className="form-select" name="time">
                              <option value={1}>1 {t('licenses.year')}</option>
                              <option value={2}>2 {t('licenses.years')}</option>
                            </Field>
                          </div>
                        </main>
                      </div>
                    </div>
                    {selectedRowIndividual && (
                      <div className="mt-3">
                        <div className="mb-2 fw-semibold">
                          {role === 'customer'
                            ? (() => {
                                const p  = selectedRowIndividual.Activations[0]?.Product
                                const t1 = parseInt(String(values.time)) === 1
                                const price = p
                                  ? (t1
                                    ? (currency === 'mxn' ? p.list_price_mx   : p.list_price)
                                    : (currency === 'mxn' ? p.list_price_mx_2 : p.list_price_2))
                                  : '0'
                                return `${price}$ ${currency}`
                              })()
                            : <span>{t('licenses.partner.charge_month')}</span>
                          }
                        </div>
                        <div className="border rounded p-2">
                          <p className="m-0">{selectedRowIndividual.product_key}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button disabled={loading} type="submit" className="btn btn-primary">
                      {t('licenses.customer.renovate')}
                      {loading && <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" />}
                    </button>
                    <button disabled={loading} type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                      {t('licenses.close')}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      {/* ── Main layout ──────────────────────────────────────────────────────── */}
      <div className="row align-items-start">

        {/* Left: action buttons */}
        <div className="col-sm-4 col-md-4 col-lg-3 mb-3">
          {role === 'customer'
            ? <Link to="link" style={pill(BRAND)} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                <FontAwesomeIcon icon={faSquarePlus}/>
                <span>{t('licenses.customer.register_licenses')}</span>
              </Link>
            : <div className="d-flex flex-column gap-2" style={{ maxWidth: 200 }}>
                <button style={pill('#22c55e')} onMouseEnter={hoverIn} onMouseLeave={hoverOut} onClick={fetchCsv}>
                  <FontAwesomeIcon icon={faFileCsv}/>
                  <span>{t('licenses.download')}</span>
                </button>
                <select
                  className="form-select form-select-sm"
                  defaultValue="10"
                  onChange={e => setLicensesCsv(e.target.value)}
                  style={{ borderRadius: 8 }}
                >
                  <option value="10">10</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="200">200</option>
                  <option value="500">500</option>
                  <option value="1000">All</option>
                </select>
              </div>
          }
        </div>

        {/* Right: custom search panel */}
        <div className="col-sm-8 col-md-8 col-lg-9 mb-3">
          <div style={{ borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <button
              type="button"
              onClick={() => setSearchOpen(prev => !prev)}
              style={{
                width        : '100%',
                display      : 'flex',
                alignItems   : 'center',
                gap          : '0.5rem',
                padding      : '0.75rem 1.25rem',
                background   : searchOpen ? '#f8fafc' : '#fff',
                border       : 'none',
                borderBottom : searchOpen ? '1px solid #e2e8f0' : 'none',
                cursor       : 'pointer',
                fontWeight   : 700,
                fontSize     : '0.9rem',
                color        : '#475569',
                textAlign    : 'left',
                transition   : 'background 0.15s',
              }}
            >
              <FontAwesomeIcon icon={faMagnifyingGlass}/>
              <span>{t('utilities.search')}</span>
              <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#94a3b8', transform: searchOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
            </button>

            {searchOpen && (
              <div style={{ padding: '1.25rem' }}>
                <Formik
                  initialValues={{ product_key: _product_key, useremail: _useremail, package: _package }}
                  onSubmit={handleSearch}
                  enableReinitialize
                >
                  <Form>
                    <div className="row align-items-center mb-3">
                      <label htmlFor="product_key" className="col-sm-2 col-form-label col-form-label-sm">{t('licenses.product_key')}</label>
                      <div className="col-sm-10">
                        <Field type="text" className="form-control form-control-sm" placeholder={t('licenses.product_key')} name="product_key" id="product_key"/>
                      </div>
                    </div>
                    <div className="row align-items-center mb-3">
                      <label htmlFor="useremail" className="col-sm-2 col-form-label col-form-label-sm">{t('licenses.user_email')}</label>
                      <div className="col-sm-10">
                        <Field type="text" className="form-control form-control-sm" placeholder={t('licenses.user_email')} name="useremail" id="useremail"/>
                      </div>
                    </div>
                    <div className="row align-items-center mb-3">
                      <label htmlFor="package" className="col-sm-2 col-form-label col-form-label-sm">{t('licenses.package')}</label>
                      <div className="col-sm-10">
                        <Field type="text" className="form-control form-control-sm" placeholder={t('licenses.package')} name="package" id="package"/>
                      </div>
                    </div>
                    <button type="submit" style={pill(BRAND)} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                      <FontAwesomeIcon icon={faMagnifyingGlass}/>
                      <span>{t('utilities.search_btn')}</span>
                    </button>
                  </Form>
                </Formik>
              </div>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="col-md-12 mb-5">
          <div className="card card-shadow" id="tableLicenses">
            <DataTable
              title=" "
              columns={columns}
              data={data}
              fixedHeader
              fixedHeaderScrollHeight="550px"
              progressPending={loading}
              paginationResetDefaultPage={resetPaginationToggle}
              pagination
              paginationPerPage={perPage}
              paginationServer
              paginationTotalRows={totalRows}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
              paginationComponentOptions={paginationComponentOptions}
              paginationRowsPerPageOptions={[50, 100, 200, 500]}
              progressComponent={<CustomLoader />}
              selectableRows={role !== 'subpartner'}
              onSelectedRowsChange={handleChange}
              clearSelectedRows={toggledClearRows}
              contextActions={contextActions}
              contextMessage={{
                singular: t('licenses.item'),
                plural  : t('licenses.items'),
                message : t('licenses.selected'),
              }}
              selectableRowDisabled={rowDisabledCriteria}
              conditionalRowStyles={conditionalRowStyles}
              dense
            />
          </div>
        </div>

      </div>
    </>
  )
}

export default Licenses
